/**
 * ScrollReveal — CSS + IntersectionObserver-powered scroll-triggered animations.
 *
 * Usage on any block wrapper:
 *   <div className="wb-reveal" data-reveal="fade-up">…</div>
 * or programmatically wrap children:
 *   <ScrollReveal type="fade-up">…</ScrollReveal>
 *
 * The stylesheet + observer are auto-registered on module import so it works
 * on any storefront page including pre-existing HTML.
 */
import React, { useEffect, useRef } from 'react';

// One-time global CSS + observer registration
let _installed = false;
function installGlobals() {
  if (_installed || typeof document === 'undefined') return;
  _installed = true;

  const style = document.createElement('style');
  style.setAttribute('data-testid', 'scroll-reveal-styles');
  style.textContent = `
    .wb-reveal { opacity: 0; transition: opacity 700ms cubic-bezier(0.16, 1, 0.3, 1), transform 700ms cubic-bezier(0.16, 1, 0.3, 1); will-change: opacity, transform; }
    .wb-reveal[data-reveal="fade-up"]     { transform: translate3d(0, 24px, 0); }
    .wb-reveal[data-reveal="fade-down"]   { transform: translate3d(0, -24px, 0); }
    .wb-reveal[data-reveal="fade-left"]   { transform: translate3d(24px, 0, 0); }
    .wb-reveal[data-reveal="fade-right"]  { transform: translate3d(-24px, 0, 0); }
    .wb-reveal[data-reveal="zoom-in"]     { transform: scale(0.94); }
    .wb-reveal[data-reveal="fade"]        { }
    .wb-reveal.wb-revealed { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
    @media (prefers-reduced-motion: reduce) {
      .wb-reveal { opacity: 1 !important; transform: none !important; transition: none !important; }
    }
  `;
  document.head.appendChild(style);

  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        e.target.classList.add('wb-revealed');
        io.unobserve(e.target);
      }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  // Attach observer to any .wb-reveal not yet observed
  const scan = () => document.querySelectorAll('.wb-reveal:not(.wb-revealed)').forEach((el) => io.observe(el));
  scan();
  // Watch DOM mutations so late-mounted blocks get observed too
  const mo = new MutationObserver(scan);
  mo.observe(document.body, { childList: true, subtree: true });
}

if (typeof window !== 'undefined') {
  // Delay to next tick so React can hydrate first
  setTimeout(installGlobals, 0);
}

export default function ScrollReveal({ type = 'fade-up', delay = 0, children, className = '', ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    installGlobals();
    if (delay && ref.current) ref.current.style.transitionDelay = `${delay}ms`;
  }, [delay]);
  return (
    <div ref={ref} className={`wb-reveal ${className}`} data-reveal={type} {...rest}>
      {children}
    </div>
  );
}
